import type { DCConditionData } from "@/workflow/components/configurations/data-center/types";
import type { Variable } from "@/workflow/components/configurations/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DOMPurify from "dompurify";
import { v4 as uuid } from "uuid";

dayjs.extend(utc);

export const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return uuid();
};

export const generateFieldId = (): string => `field_${generateUUID()}`;

export const decodeHtml = (html: string) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

export const sanitizeHTML = (content: string): string => {
  return JSON.stringify(content)
    .trim()
    .replaceAll(/^"|"$/g, "")
    .replace(/<p>"<\/p>$/, "")
    .replaceAll(String.raw`\"`, '"')
    .replaceAll(String.raw`\n`, "\n")
    .replaceAll(String.raw`\'`, "'")
    .replaceAll('<br class="ProseMirror-trailingBreak">', "")
    .replace(/<p\s*\/>/g, "<p></p>");
};

export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length !== 8) return phone;
  return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
};

export function isHtmlEmpty(html: string): boolean {
  const safeHtml = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

  const tmp = document.createElement("div");
  tmp.innerHTML = safeHtml;

  const textContent = tmp.textContent || "";
  return textContent.trim().length === 0;
}

export function cleanExtraLineBreaks(html: string) {
  return html
    .replace(/\n{2,}/g, "\n")
    .replace(/>[^\S\n]*\n[^\S\n]*</g, "><")
    .trim();
}

export const cleanTitap = (value: string) => {
  return value
    .replaceAll('<br class="ProseMirror-trailingBreak">', "")
    .replaceAll(/<p([^>]*)\/>/g, "<p$1></p>");
};

/**
 * Normalizes a date string to YYYY-MM-DD format
 * Accepts both YYYY-MM-DD and ISO string formats
 * This function is timezone-agnostic and avoids all timezone-related issues
 *
 * @param dateValue - The date string to normalize
 * @returns The normalized date string in YYYY-MM-DD format, or the original value if not a valid date
 */
export const formatDate = (dateValue: string): string => {
  try {
    // Check if it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      // Validate it's a real date by checking if it can be parsed
      const testDate = new Date(dateValue + "T00:00:00.000Z");
      if (Number.isNaN(testDate.getTime())) {
        return dateValue; // Return original if invalid
      }
      return dateValue;
    }

    // Handle ISO string formats (with or without timezone info)
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateValue)) {
      // Extract just the date part from ISO string to avoid timezone issues
      const datePart = dateValue.substring(0, 10);

      // Validate the extracted date part
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const testDate = new Date(datePart + "T00:00:00.000Z");
        if (!Number.isNaN(testDate.getTime())) {
          return datePart;
        }
      }
    }

    // For other formats, try to parse but be careful about timezones
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return dateValue; // Return original if not a valid date
    }

    // If the original string looks like it has timezone info, use UTC methods
    // Otherwise, use local methods to preserve the intended date
    const hasTimezoneInfo = /Z$|[+-]\d{2}:?\d{2}$/.test(dateValue);

    let year: number, month: number, day: number;

    if (hasTimezoneInfo) {
      // Use UTC methods for timezone-aware strings
      year = date.getUTCFullYear();
      month = date.getUTCMonth() + 1;
      day = date.getUTCDate();
    } else {
      // Use local methods for strings without timezone info
      year = date.getFullYear();
      month = date.getMonth() + 1;
      day = date.getDate();
    }

    const monthStr = String(month).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");

    return `${year}-${monthStr}-${dayStr}`;
  } catch {
    // If any error occurs, return the original value
    return dateValue;
  }
};

// Formats a backend date (SQL datetime or ISO 8601) to the form-engine format:
// DD/MM/YYYY for date, DD/MM/YYYY HH:mm for datetime. Parsed as UTC so the day
// is not shifted by the local timezone. Non-dates are returned unchanged.
export const formatExpedientDate = (
  value: string,
  withTime = false,
): string => {
  // Only ISO/SQL-shaped values, so numbers or text aren't parsed as dates.
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
  const iso =
    value.includes(" ") && !value.includes("T")
      ? value.replace(" ", "T")
      : value;
  const parsed = dayjs.utc(iso);
  if (!parsed.isValid()) return value;
  return parsed.format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

/**
 * Verify if a numeric range condition is valid
 *
 * @param condition
 * @returns
 */
export const isValidNumericRange = (condition: DCConditionData): boolean => {
  if (!condition.operation?.needsComparativeRange) return true;

  if (!condition.initialRangeValue || !condition.endRangeValue) return true;

  const getNumericValue = (value: string | Variable): number | null => {
    if (typeof value === "string") {
      const num = Number.parseFloat(value);
      return Number.isNaN(num) ? null : num;
    }
    return null; //variables do not apply for numeric validation
  };
  const initialNum = getNumericValue(condition.initialRangeValue);
  const endNum = getNumericValue(condition.endRangeValue);
  if (initialNum === null || endNum === null) return true;
  return endNum > initialNum;
};

export const toCapitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const parseCharacters = (str: string) => {
  if (!str) return "";
  const normalized = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const specialCases = {
    æ: "ae",
    Æ: "AE",
    œ: "oe",
    Œ: "OE",
    ø: "o",
    Ø: "O",
    ß: "ss",
    ð: "d",
    Ð: "D",
    þ: "th",
    Þ: "TH",
    ł: "l",
    Ł: "L",
    "¿": "",
    "¡": "",
    "«": '"',
    "»": '"',
    '"': '"',
    '\\"': '"',
    "–": "-",
    "—": "-",
    "…": "...",
  };

  let result = normalized;
  for (const [special, replacement] of Object.entries(specialCases)) {
    result = result.replace(new RegExp(special, "g"), replacement);
  }
  // Remove any remaining non-alphanumeric characters except spaces, hyphens, and underscores
  result = result.replace(/[^a-zA-Z0-9 _-]/g, "");
  // Replace spaces with underscores and convert to lowercase
  result = result.replace(/\s+/g, "_").toLowerCase();
  return result;
};

/**
 * Get the verb form of an operation in spanish
 * @param operation
 * @returns
 */
export const getOperationVerb = (operation: string) => {
  switch (operation) {
    case "create":
      return "creados";
    case "update":
      return "actualizados";
    case "delete":
      return "borrados";
    default:
      return "";
  }
};

export const joinTitles = (titles: string[]) => {
  const normalizedTitles = titles.map((title, index) => {
    if (!title) return "";
    if (index === 0) return title;
    return title.charAt(0).toLowerCase().concat(title.slice(1));
  });

  return normalizedTitles
    .slice(0, titles.length - 1)
    .join(", ")
    .concat(normalizedTitles.length > 1 ? " y " : "")
    .concat(normalizedTitles.at(-1) || "");
};

/**
 * Convierte cualquier valor a texto sin caer en la representación por defecto
 * de los objetos ("[object Object]"). Los objetos y arrays se serializan a JSON.
 *
 * @param value - Valor de tipo desconocido a representar como texto
 * @param fallback - Texto a devolver cuando el valor es null/undefined
 * @returns El valor como string legible
 */
export const toDisplayString = (value: unknown, fallback = ""): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? fallback;
  } catch {
    return fallback;
  }
};

/**
 * Limpia las etiquetas HTML de un texto y devuelve solo el contenido de texto plano
 * @param html - String que puede contener HTML
 * @returns String sin etiquetas HTML
 */
export const stripHtmlTags = (html: string | null | undefined): string => {
  if (!html || typeof html !== "string") {
    return "";
  }
  return (
    new DOMParser()
      .parseFromString(html, "text/html")
      .body.textContent?.trim() ?? ""
  );
};

/**
 * Formatea el nombre de una acción de interoperabilidad para mostrarlo de manera legible al usuario.
 * @param actionName Nombre de la acción de interoperabilidad a formatear
 * @returns Nombre de la acción formateado para mostrar al usuario
 */
export const formatInteropActionName = (actionName: string): string => {
  if (!actionName) return "";
  return actionName === "personal_information"
    ? "Información Personal"
    : actionName
        .replaceAll("_", " ")
        .replace(/\bdui\b/gi, "DUI")
        .replace(/\bnit\b/gi, "NIT")
        .replace(/\bmh\b/gi, "MH")
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const safeStringify = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return String(value);
  if (typeof value === "object") return JSON.stringify(value);
  return "";
};
